import { type BrokerSelection, type Capability, modelSaid } from "@rally/agreed";
import { Chip, Col, DropdownMenu, Icon, ListAction, ListRow, Row, Text, useWhenItWas } from "@rally/ui-kit";
import { useState } from "react";
import { thinkingLabel } from "../localization/labels.ts";
import { say } from "../localization/say.ts";
import { goTo } from "../router.ts";
import { faceOfAnAgent } from "./an-agents-mark.ts";
import { calledBy } from "./called.ts";
import { ConfigureAgent } from "./configure-agent.tsx";
import { useFace } from "./face.ts";
import { RenameAgent } from "./rename-agent.tsx";
import { cannotSearchYet } from "./searching.ts";

export type Agent = {
  account: string;
  handle: string;
  displayName: string | null;
  avatar: string | null;
  model: string;
  credential: string | null;
  broker?: BrokerSelection | null;
  provider: string | null;
  thinking: string;

  capabilities: Capability[];

  tools: boolean;
  toolFlags: string[];
  owner: string;
  kind: "system" | "built_in" | "custom";
  definition: string;
  shares: Array<{ scope: "project" | "conversation" | "server"; id: string | null }>;
  lately: { state: "working" | "stopped" | "finished"; at: string; conversation: string } | null;
};

const LATELY_MARK = { working: "online", stopped: "away", finished: "offline" } as const;

function latelySaid(lately: NonNullable<Agent["lately"]>, whenItWas: (at: Date) => string): string {
  const when = whenItWas(new Date(lately.at));
  if (lately.state === "working") return say("agents.workingNow");
  if (lately.state === "stopped") return say("agents.stoppedAgo")(when);
  return say("agents.finishedAgo")(when);
}

type Reach = { said: string };

function reachOf(agent: Agent): Reach {
  if (agent.shares.some((one) => one.scope === "server")) return { said: say("agents.sharedServer") };
  if (agent.shares.length > 0) return { said: say("agents.sharedRooms")(agent.shares.length) };
  return { said: say("agents.private") };
}

function theLineUnderTheName(agent: Agent): string {
  return `${modelSaid(agent.model)} \u00b7 ${thinkingLabel(agent.thinking)}`;
}

export function AgentRow({
  agent,
  searchKeys,
  yours,
  runsIt,
  last,
  onChanged,
  onShare,
  onRemove,
}: {
  agent: Agent;

  searchKeys: number;

  yours: boolean;

  runsIt: boolean;

  last?: boolean | undefined;

  onChanged: () => void;
  onShare: () => void;
  onRemove: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const face = useFace(agent.avatar);
  const whenItWas = useWhenItWas();
  const marks = faceOfAnAgent(agent.model, agent.provider);
  const called = calledBy(agent) ?? agent.handle;
  const reach = reachOf(agent);
  const shared = agent.shares.some((one) => one.scope === "server");
  const mayEdit = yours || runsIt;
  const wanting = cannotSearchYet({ capabilities: agent.capabilities, searchKeys });

  const more = yours
    ? [
        { label: shared ? say("agents.unshare") : say("agents.share"), onPress: onShare },
        { label: say("agents.remove"), onPress: onRemove, danger: true },
      ]
    : [];

  return (
    <>
      <ListRow
        testID={`agent-${agent.handle}`}
        face={{
          name: called,
          src: face,
          ...marks,
          tone: face || marks.glyph ? "neutral" : "accent",
          ...(agent.lately ? { status: LATELY_MARK[agent.lately.state] } : {}),
        }}
        title={called}
        tag={
          <Text typeName="caption" color="text.tertiary">
            @{agent.handle}
          </Text>
        }
        value={`${theLineUnderTheName(agent)} · ${reach.said}`}
        note={
          agent.lately || wanting ? (
            <Row gap={2} wrap>
              {agent.lately ? (
                <Chip
                  size="sm"
                  muted
                  icon={agent.lately.state === "working" ? "activity" : "clock"}
                  onPress={() => goTo(`/c/${agent.lately?.conversation}`)}
                  testID={`agent-lately-${agent.handle}`}
                >
                  {latelySaid(agent.lately, whenItWas)}
                </Chip>
              ) : null}
              {wanting ? (
                <Text typeName="caption" color="warning" testID={`agent-cannot-search-${agent.handle}`}>
                  {say("agent.cannotSearchYet")}
                </Text>
              ) : null}
            </Row>
          ) : undefined
        }
        action={
          more.length > 0 ? (
            <DropdownMenu
              items={more}
              align="right"
              accessibilityLabel={say("agents.moreActions")(called)}
              testID={`agent-more-${agent.handle}`}
              trigger={
                <Col style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}>
                  <Icon name="more-horizontal" size={18} color="text.tertiary" />
                </Col>
              }
            />
          ) : mayEdit ? (
            <ListAction
              label={say("agent.configure")}
              onPress={() => setConfiguring(true)}
              testID={`agent-configure-${agent.handle}`}
            />
          ) : undefined
        }
        onPress={mayEdit ? () => setConfiguring(true) : undefined}
        last={last ?? false}
      />
      <RenameAgent
        account={agent.account}
        called={called}
        name={agent.displayName}
        visible={renaming}
        onDismiss={() => setRenaming(false)}
        onRenamed={onChanged}
      />
      {configuring ? (
        <ConfigureAgent
          key={agent.account}
          agent={agent}
          searchKeys={searchKeys}
          visible
          onDismiss={() => setConfiguring(false)}
          onChanged={onChanged}
        />
      ) : null}
    </>
  );
}
